import React, {
  ForwardedRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  PixelRatio,
  Platform,
  SectionList,
  View,
} from "react-native";
import { graphql, useFragment } from "react-relay";
import { TodoSubject } from "./TodoSubject/TodoSubject";
import { AHSeparator, Box, useScaleFactor, useTodoSorter } from "arkh";
import { TodoOverviewListItemMedium } from "./TodoOverviewListItemMedium";
import { WidgetTodoOverviewListRef } from "./WidgetTodoOverviewContentMedium";
import { WidgetTodoOverviewContentMediumGrouped_WidgetTodoOverviewContentMediumGrouped$key } from "./__generated__/WidgetTodoOverviewContentMediumGrouped_WidgetTodoOverviewContentMediumGrouped.graphql";
import { useSubscriptionWithSkip } from "../utils/useSubscriptionWithSkip";
import { WidgetTodoOverviewContentMediumGrouped_WidgetTodoOverviewContentMediumGroupedSubscription } from "./__generated__/WidgetTodoOverviewContentMediumGrouped_WidgetTodoOverviewContentMediumGroupedSubscription.graphql";

function WidgetTodoOverviewContentMediumGroupedC(
  props: {
    content: WidgetTodoOverviewContentMediumGrouped_WidgetTodoOverviewContentMediumGrouped$key;
    highlightedItemId?: string;
    onPressItem: (id: string) => void;
    setScroll: (itemId: string) => void;
    interactive?: boolean;
    isFocused?: boolean;
    shouldDimInactive: boolean;
  },
  ref: ForwardedRef<WidgetTodoOverviewListRef>
) {
  const content = useFragment(
    graphql`
      fragment WidgetTodoOverviewContentMediumGrouped_WidgetTodoOverviewContentMediumGrouped on WidgetTodoOverviewContentMediumGrouped {
        __typename
        id
        subscriptionKey
        subjectGroups {
          subject {
            ...TodoSubject_TodoSubject
          }
          todos {
            id
            ordinal
            status
            ...TodoOverviewListItemMedium_Todo
          }
        }
      }
    `,
    props.content
  );

  useSubscriptionWithSkip<WidgetTodoOverviewContentMediumGrouped_WidgetTodoOverviewContentMediumGroupedSubscription>(
    useMemo(
      () => ({
        variables: {
          subscriptionKey: content.subscriptionKey,
        },
        subscription: graphql`
          subscription WidgetTodoOverviewContentMediumGrouped_WidgetTodoOverviewContentMediumGroupedSubscription(
            $subscriptionKey: String!
          ) {
            widgetTodoOverviewGroupedContentChanged(
              subscriptionKey: $subscriptionKey
            ) {
              ... on WidgetTodoOverviewContentMediumGrouped {
                ...WidgetTodoOverviewContentMediumGrouped_WidgetTodoOverviewContentMediumGrouped
              }
              ... on Todo {
                id
                ordinal
                ...TodoOverviewListItemMedium_Todo
              }
            }
          }
        `,
      }),
      [content.subscriptionKey]
    ),
    {
      skip: props.interactive === false,
    }
  );

  const sectionListRef = useRef<SectionList>(null);

  const sections = useMemo(
    () =>
      content.subjectGroups.map((subjectGroup) => ({
        subject: subjectGroup.subject,
        data: subjectGroup.todos.filter((t) => t?.id),
      })),
    [content]
  );

  const flattenedList = useMemo(() => {
    return sections.reduce((acc: any, section) => {
      return [...acc, section.subject, ...section.data];
    }, []);
  }, [sections]);

  useImperativeHandle(ref, () => ({
    scrollToItem: (itemId: string | null) => {
      const foundItem = itemId
        ? sections
            .flatMap((s, sIndex) =>
              s.data.map((d, itemIndex) => [d.id, sIndex, itemIndex] as const)
            )
            .find(([id]) => id === itemId)
        : null;

      if (foundItem) {
        sectionListRef.current?.scrollToLocation({
          itemIndex: foundItem[2],
          sectionIndex: foundItem[1],
        });
      } else {
        sectionListRef.current?.scrollToLocation({
          itemIndex: 0,
          sectionIndex: 0,
        });
      }
    },
  }));

  const sf = useScaleFactor();
  const ITEM_HEIGHT = useMemo(
    () => PixelRatio.roundToNearestPixel(40 * sf),
    [sf]
  );

  const getItemLayout = useCallback(
    (_data: any, index: number) => ({
      length: ITEM_HEIGHT,
      offset: ITEM_HEIGHT * index,
      index,
    }),
    [ITEM_HEIGHT]
  );

  const setScroll = props.setScroll;
  const lastScrollId = useRef<string | null>(null);
  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const index = Math.max(
        0,
        Math.floor(
          (event.nativeEvent.targetContentOffset
            ? event.nativeEvent.targetContentOffset.y
            : event.nativeEvent.contentOffset.y) /
            ITEM_HEIGHT -
            1
        )
      );
      const item = flattenedList[index];
      if (item && lastScrollId.current !== item.id) {
        setScroll(item.id);
        lastScrollId.current = item.id;
      }
    },
    [ITEM_HEIGHT, flattenedList, setScroll]
  );

  const shared = useMemo(
    () => ({
      opacity: props.shouldDimInactive ? 0.2 : 1,
    }),
    [props.shouldDimInactive]
  );

  const renderItem = useCallback(
    ({ item }: { item: any }) => {
      return (
        <View style={item.status !== "PENDING" ? shared : undefined}>
          <TodoOverviewListItemMedium
            breadcrumbsExcludeRoot
            isFocused={item.id === props.highlightedItemId}
            onPress={props.onPressItem}
            todo={item}
          />
        </View>
      );
    },
    [props.highlightedItemId, props.onPressItem, shared]
  );
  const renderSectionHeader = useCallback(
    ({ section }: any) => (
      <Box color="surface">
        <AHSeparator spaceBottom="xsmall" />
        {section.subject ? (
          <Box spaceHorizontal="small" color="surface" spaceVertical="xsmall">
            <TodoSubject highlightLast subject={section.subject} />
          </Box>
        ) : null}
      </Box>
    ),
    []
  );

  return (
    <SectionList
      ref={sectionListRef}
      getItemLayout={getItemLayout}
      sections={sections}
      initialNumToRender={10}
      scrollEventThrottle={200}
      stickySectionHeadersEnabled={false}
      showsVerticalScrollIndicator={Platform.OS !== "web"}
      onScroll={props.isFocused ? onScroll : undefined}
      scrollEnabled={!(props.interactive === false)}
      renderItem={renderItem}
      renderSectionHeader={renderSectionHeader}
      keyExtractor={keyExtractor}
    />
  );
}

function keyExtractor(item: { id: string }) {
  return item.id;
}

export const WidgetTodoOverviewContentMediumGrouped = React.memo(
  React.forwardRef(WidgetTodoOverviewContentMediumGroupedC)
);
